/**
 * MaterialPropertiesEditor — pure, value-in / patch-out PBR editor.
 *
 * Owns every control for the full SceneMaterial surface: base PBR, grid
 * overlay, emissive, surface, coating, sheen, and glass/transmission. Has
 * no store access — the caller wires onChange to whichever updater is
 * appropriate for the context (sidebar → updateSceneObjectMaterial, gallery
 * scene tab → updateMaterial).
 *
 * This is the single source of truth for material UI across the app. Do
 * not duplicate its controls elsewhere; pass it a (properties, onChange)
 * pair instead.
 */

import { ChevronDown, ChevronRight } from "lucide-react";
import { useCallback, useState } from "react";
import type { SceneMaterial } from "../../modeling/types.ts";
import { ColorPicker } from "../ColorPicker.tsx";
import { CompactRow, SliderRow } from "../shared.tsx";
import {
  COLOR_ACCENT,
  COLOR_BORDER,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
} from "../tokens.ts";

// ---------------------------------------------------------------------------
// Collapsible sub-group
// ---------------------------------------------------------------------------

function MaterialGroup({
  label,
  defaultOpen = false,
  children,
}: {
  label: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = useCallback(() => setOpen((p) => !p), []);

  return (
    <div
      style={{
        borderTop: `0.5px solid ${COLOR_BORDER}`,
        marginTop: 6,
        paddingTop: 4,
      }}
    >
      <div
        onClick={toggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <span
          style={{
            color: COLOR_TEXT_FAINT,
            display: "flex",
            alignItems: "center",
          }}
        >
          {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
        </span>
        <span
          style={{
            fontSize: FONT_SIZE_TINY,
            color: COLOR_TEXT_DIM,
            textTransform: "uppercase",
            letterSpacing: 0.8,
          }}
        >
          {label}
        </span>
      </div>
      {open && <div style={{ marginTop: 2 }}>{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Percent slider — maps 0–1 value to 0–100 UI
// ---------------------------------------------------------------------------

function PercentSliderRow({
  label,
  value,
  onChange,
  max = 100,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  max?: number;
}) {
  const percent = Math.round(value * 100);
  return (
    <SliderRow
      density="compact"
      label={label}
      value={percent}
      min={0}
      max={max}
      displayValue={`${percent}%`}
      onChange={(v) => onChange(v / 100)}
      dimChromeOnDrag
    />
  );
}

// ---------------------------------------------------------------------------
// Compact segmented picker — a small inline alternative to SegmentedControl
// sized to fit inside a CompactRow.
// ---------------------------------------------------------------------------

function CompactSegmented<T extends string | number>({
  options,
  value,
  onChange,
}: {
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: 2,
        border: `1px solid ${COLOR_BORDER}`,
        borderRadius: 3,
        overflow: "hidden",
        flex: 1,
      }}
    >
      {options.map((opt) => {
        const active =
          typeof opt.value === "number" && typeof value === "number"
            ? Math.abs(value - opt.value) < 1e-6
            : opt.value === value;
        return (
          <div
            key={String(opt.value)}
            onClick={() => onChange(opt.value)}
            style={{
              flex: 1,
              textAlign: "center",
              padding: "2px 0",
              fontSize: FONT_SIZE_SMALL,
              cursor: "pointer",
              userSelect: "none",
              background: active ? COLOR_ACCENT : "transparent",
              color: active ? "#000" : COLOR_TEXT_DIM,
            }}
          >
            {opt.label}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pattern overlay constants
// ---------------------------------------------------------------------------

/**
 * Pattern picker uses a sentinel "off" value to represent the disabled
 * (gridPattern: undefined) state. The sentinel never round-trips through the
 * material: selecting "off" clears the field.
 */
type GridPatternChoice = "off" | "uniform" | "brick" | "tile";

const GRID_PATTERN_OPTIONS: ReadonlyArray<{
  value: GridPatternChoice;
  label: string;
}> = [
  { value: "off", label: "Off" },
  { value: "uniform", label: "Grid" },
  { value: "brick", label: "Brick" },
  { value: "tile", label: "Tile" },
];

/** Cell size options and defaults per pattern type. */
const GRID_CELL_OPTIONS: Record<
  Exclude<GridPatternChoice, "off">,
  ReadonlyArray<{ value: number; label: string }>
> = {
  uniform: [
    { value: 0.01, label: "1 cm" },
    { value: 0.1, label: "10 cm" },
    { value: 1, label: "1 m" },
  ],
  brick: [
    { value: 0.05, label: "5 cm" },
    { value: 0.075, label: "7.5 cm" },
    { value: 0.15, label: "15 cm" },
  ],
  tile: [
    { value: 0.1, label: "10 cm" },
    { value: 0.3, label: "30 cm" },
    { value: 0.6, label: "60 cm" },
  ],
};

/** Default cell size in meters used when gridCellSize is unset. */
const DEFAULT_CELL_SIZE: Record<Exclude<GridPatternChoice, "off">, number> = {
  uniform: 1,
  brick: 0.075,
  tile: 0.3,
};

/**
 * Projection picker uses a sentinel "auto" value to represent the
 * "no explicit setting — inherit from primitive kind" state. The sentinel
 * never round-trips through the material: selecting "Auto" clears the
 * field by patching `gridProjection: undefined`.
 */
type GridProjectionChoice = "auto" | "tangent" | "triplanar";

const GRID_PROJECTION_OPTIONS: ReadonlyArray<{
  value: GridProjectionChoice;
  label: string;
}> = [
  { value: "auto", label: "Auto" },
  { value: "tangent", label: "Tangent" },
  { value: "triplanar", label: "Triplanar" },
];

const GRID_LINES_OPTIONS: ReadonlyArray<{
  value: "both" | "h" | "v";
  label: string;
}> = [
  { value: "both", label: "Both" },
  { value: "h", label: "H" },
  { value: "v", label: "V" },
];

const GRID_ROTATION_OPTIONS: ReadonlyArray<{
  value: 0 | 45 | 90;
  label: string;
}> = [
  { value: 0, label: "0°" },
  { value: 45, label: "45°" },
  { value: 90, label: "90°" },
];

// ---------------------------------------------------------------------------
// MaterialPropertiesEditor
// ---------------------------------------------------------------------------

export interface MaterialPropertiesEditorProps {
  properties: SceneMaterial;
  onChange: (patch: Partial<SceneMaterial>) => void;
}

export function MaterialPropertiesEditor({
  properties: mat,
  onChange,
}: MaterialPropertiesEditorProps) {
  return (
    <>
      {/* Base PBR — always visible */}
      <CompactRow label="Color">
        <ColorPicker
          value={mat.color}
          onChange={(hex) => onChange({ color: hex })}
        />
      </CompactRow>
      <CompactRow label="Pattern">
        <CompactSegmented<GridPatternChoice>
          options={GRID_PATTERN_OPTIONS}
          value={mat.gridPattern ?? "off"}
          onChange={(v) =>
            onChange({
              gridPattern: v === "off" ? undefined : v,
              // Reset cell size when switching patterns so the segmented
              // picker always reflects a valid option for the new pattern.
              gridCellSize: undefined,
            })
          }
        />
      </CompactRow>
      {mat.gridPattern && (
        <>
          {/* Cell size — options and default differ per pattern */}
          <CompactRow label="Cell">
            <CompactSegmented<number>
              options={GRID_CELL_OPTIONS[mat.gridPattern]}
              value={mat.gridCellSize ?? DEFAULT_CELL_SIZE[mat.gridPattern]}
              onChange={(v) => onChange({ gridCellSize: v })}
            />
          </CompactRow>
          <CompactRow label="Rotate">
            <CompactSegmented<0 | 45 | 90>
              options={GRID_ROTATION_OPTIONS}
              value={mat.gridRotation ?? 0}
              onChange={(v) =>
                onChange({ gridRotation: v === 0 ? undefined : v })
              }
            />
          </CompactRow>
          <CompactRow label="Project">
            <CompactSegmented<GridProjectionChoice>
              options={GRID_PROJECTION_OPTIONS}
              value={mat.gridProjection ?? "auto"}
              onChange={(v) =>
                onChange({ gridProjection: v === "auto" ? undefined : v })
              }
            />
          </CompactRow>
          <PercentSliderRow
            label="Opacity"
            value={mat.gridOpacity ?? 0.6}
            onChange={(v) => onChange({ gridOpacity: v })}
          />
          {/* Uniform: line direction + color */}
          {mat.gridPattern === "uniform" && (
            <>
              <CompactRow label="Lines">
                <CompactSegmented<"both" | "h" | "v">
                  options={GRID_LINES_OPTIONS}
                  value={mat.gridLines ?? "both"}
                  onChange={(v) => onChange({ gridLines: v })}
                />
              </CompactRow>
              <CompactRow label="Color">
                <ColorPicker
                  value={mat.gridColor ?? "#ffffff"}
                  onChange={(hex) => onChange({ gridColor: hex })}
                />
              </CompactRow>
            </>
          )}
          {/* Brick / Tile: grout color + joint thickness */}
          {(mat.gridPattern === "brick" || mat.gridPattern === "tile") && (
            <>
              <CompactRow label="Grout">
                <ColorPicker
                  value={mat.gridColor ?? "#888888"}
                  onChange={(hex) => onChange({ gridColor: hex })}
                />
              </CompactRow>
              <PercentSliderRow
                label="Joint"
                value={mat.gridGroutThickness ?? 0.06}
                onChange={(v) => onChange({ gridGroutThickness: v })}
                max={50}
              />
              <SliderRow
                density="compact"
                label="Bump"
                value={Math.round((mat.gridBumpScale ?? 0) * 1000)}
                min={0}
                max={100}
                displayValue={(mat.gridBumpScale ?? 0).toFixed(3)}
                parseValue={(raw) => parseFloat(raw) * 1000}
                onChange={(v) => onChange({ gridBumpScale: v / 1000 })}
                dimChromeOnDrag
              />
            </>
          )}
        </>
      )}
      <PercentSliderRow
        label="Roughness"
        value={mat.roughness}
        onChange={(v) => onChange({ roughness: v })}
      />
      <PercentSliderRow
        label="Metalness"
        value={mat.metalness}
        onChange={(v) => onChange({ metalness: v })}
      />

      {/* Emissive */}
      <MaterialGroup label="Emissive">
        <CompactRow label="Color">
          <ColorPicker
            value={mat.emissiveColor ?? "#000000"}
            onChange={(hex) => onChange({ emissiveColor: hex })}
          />
        </CompactRow>
        <SliderRow
          density="compact"
          label="Intensity"
          value={Math.round((mat.emissiveIntensity ?? 0) * 100)}
          min={0}
          max={500}
          displayValue={(mat.emissiveIntensity ?? 0).toFixed(1)}
          parseValue={(raw) => parseFloat(raw) * 100}
          onChange={(v) => onChange({ emissiveIntensity: v / 100 })}
          dimChromeOnDrag
        />
      </MaterialGroup>

      {/* Surface */}
      <MaterialGroup label="Surface">
        <PercentSliderRow
          label="Opacity"
          value={mat.opacity ?? 1}
          onChange={(v) => onChange({ opacity: v })}
        />
        <PercentSliderRow
          label="Specular"
          value={mat.specularIntensity ?? 0.5}
          onChange={(v) => onChange({ specularIntensity: v })}
        />
        <SliderRow
          density="compact"
          label="Normal"
          value={Math.round((mat.normalIntensity ?? 1) * 100)}
          min={0}
          max={300}
          displayValue={(mat.normalIntensity ?? 1).toFixed(1)}
          parseValue={(raw) => parseFloat(raw) * 100}
          onChange={(v) => onChange({ normalIntensity: v / 100 })}
          dimChromeOnDrag
        />
      </MaterialGroup>

      {/* Coating */}
      <MaterialGroup label="Coating">
        <PercentSliderRow
          label="Clearcoat"
          value={mat.clearcoat ?? 0}
          onChange={(v) => onChange({ clearcoat: v })}
        />
        <PercentSliderRow
          label="Roughness"
          value={mat.clearcoatRoughness ?? 0}
          onChange={(v) => onChange({ clearcoatRoughness: v })}
        />
      </MaterialGroup>

      {/* Sheen */}
      <MaterialGroup label="Sheen">
        <PercentSliderRow
          label="Sheen"
          value={mat.sheen ?? 0}
          onChange={(v) => onChange({ sheen: v })}
        />
        <PercentSliderRow
          label="Roughness"
          value={mat.sheenRoughness ?? 1}
          onChange={(v) => onChange({ sheenRoughness: v })}
        />
        <CompactRow label="Color">
          <ColorPicker
            value={mat.sheenColor ?? "#000000"}
            onChange={(hex) => onChange({ sheenColor: hex })}
          />
        </CompactRow>
      </MaterialGroup>

      {/* Glass / Transmission */}
      <MaterialGroup label="Glass">
        <PercentSliderRow
          label="Transmit"
          value={mat.transmission ?? 0}
          onChange={(v) => onChange({ transmission: v })}
        />
        <SliderRow
          density="compact"
          label="IOR"
          value={Math.round((mat.ior ?? 1.5) * 100)}
          min={100}
          max={250}
          displayValue={(mat.ior ?? 1.5).toFixed(2)}
          parseValue={(raw) => parseFloat(raw) * 100}
          onChange={(v) => onChange({ ior: v / 100 })}
          dimChromeOnDrag
        />
        <SliderRow
          density="compact"
          label="Thickness"
          value={Math.round((mat.thickness ?? 0) * 10)}
          min={0}
          max={100}
          displayValue={(mat.thickness ?? 0).toFixed(1)}
          parseValue={(raw) => parseFloat(raw) * 10}
          onChange={(v) => onChange({ thickness: v / 10 })}
          dimChromeOnDrag
        />
      </MaterialGroup>
    </>
  );
}
